import React, { useEffect, useMemo, useState } from "react";
import "./Css/themes/base/all.css";
import "../App.css";
import "./Css/trafico.css";
import axios from "../utils/axiosConfig";
import BarraTrafico from "../Modulos/BarraTrafico";
import LivesGridManager from "../components/enVivo/LivesGridManager";
import BaseModalXXL from "../components/modals/baseModalXXL";
import EnVivoPanel from "../components/enVivo/EnVivoPanel";
import { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const ACTIVE_STREAMS_STORAGE_KEY = "active_streams_v1";

const syncActiveStreamsToStorage = (streams = []) => {
  try {
    const payload = JSON.stringify(Array.isArray(streams) ? streams : []);
    sessionStorage.setItem(ACTIVE_STREAMS_STORAGE_KEY, payload);
    localStorage.setItem(ACTIVE_STREAMS_STORAGE_KEY, payload);
  } catch {
    // noop
  }
};

const isStreamingOn = (contrato) => {
  const value = contrato?.StreamingOn ?? contrato?.streamingOn;
  return value === true || value === "true" || value === 1 || value === "1";
};

const getContratoStreamingRawUrl = (contrato) => {
  if (!contrato || typeof contrato !== "object") return "";
  return (
    contrato.StreamingUrl ||
    contrato.streamingUrl ||
    contrato.StreamUrl ||
    contrato.streamUrl ||
    contrato.Url ||
    contrato.URL ||
    contrato.url ||
    ""
  );
};

const parseStreamingUrls = (rawUrl) => {
  if (!rawUrl) return [];
  return String(rawUrl)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const resolveLiveStreamUrl = (msg, deviceId) => {
  const raw =
    msg?.streamId ||
    msg?.streamUrl ||
    msg?.url ||
    msg?.data?.url ||
    "";

  const normalizeName = (value) => {
    if (!value) return "";
    const text = String(value);
    const playMatch = text.match(/play\.html\?name=([^&]+)/i);
    if (playMatch?.[1]) return playMatch[1];
    const liveAppMatch = text.match(/LiveApp\/([^/?#]+)/i);
    if (liveAppMatch?.[1]) return liveAppMatch[1];
    if (text.includes("/")) {
      const last = text.split("/").filter(Boolean).pop();
      if (last) return last;
    }
    return text;
  };

  if (raw && String(raw).toLowerCase().includes("play.html")) return raw;

  const nameFromRaw = normalizeName(raw);
  if (nameFromRaw) {
    return `https://streaming.cargotronics.com:5443//LiveApp/play.html?name=${nameFromRaw}`;
  }

  if (deviceId) {
    return `https://streaming.cargotronics.com:5443//LiveApp/play.html?name=${deviceId}_JT800`;
  }

  return "";
};

const buildActiveStreamsFromContratos = (contratos = []) => {
  if (!Array.isArray(contratos)) return [];

  const byDeviceAndStream = new Map();

  contratos.forEach((contrato) => {
    if (!isStreamingOn(contrato)) return;

    const deviceId = String(contrato?.FKLokDeviceID || "").trim();
    if (!deviceId) return;

    const rawUrl = getContratoStreamingRawUrl(contrato);
    const parsedUrls = parseStreamingUrls(rawUrl);
    const candidates = parsedUrls.length > 0 ? parsedUrls : [rawUrl];

    candidates.forEach((candidate) => {
      const streamId = String(
        resolveLiveStreamUrl(
          { streamId: candidate, streamUrl: candidate, url: candidate },
          deviceId
        ) || candidate || ""
      ).trim();

      if (!streamId) return;

      const key = `${deviceId}::${streamId}`;
      byDeviceAndStream.set(key, {
        deviceId,
        streamId,
        timestamp: contrato?.LoksysServerTime || new Date().toISOString(),
      });
    });
  });

  return Array.from(byDeviceAndStream.values());
};

const readActiveStreamsFromSession = () => {
  const tryParse = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return null;
    }
  };

  try {
    const sessionParsed = tryParse(sessionStorage.getItem(ACTIVE_STREAMS_STORAGE_KEY));
    if (sessionParsed) return sessionParsed;

    const localParsed = tryParse(localStorage.getItem(ACTIVE_STREAMS_STORAGE_KEY));
    if (localParsed) {
      sessionStorage.setItem(ACTIVE_STREAMS_STORAGE_KEY, JSON.stringify(localParsed));
      return localParsed;
    }

    return [];
  } catch {
    return [];
  }
};

const normalizeStreams = (list = []) =>
  (Array.isArray(list) ? list : [])
    .map((item) => ({
      deviceId: String(item?.deviceId || ""),
      streamId: String(item?.streamId || ""),
      streamUrl: String(item?.streamUrl || item?.url || ""),
    }))
    .sort((a, b) =>
      `${a.deviceId}|${a.streamId}|${a.streamUrl}`.localeCompare(
        `${b.deviceId}|${b.streamId}|${b.streamUrl}`
      )
    );

const areStreamsEqual = (a = [], b = []) => {
  const na = normalizeStreams(a);
  const nb = normalizeStreams(b);
  if (na.length !== nb.length) return false;
  for (let i = 0; i < na.length; i += 1) {
    if (
      na[i].deviceId !== nb[i].deviceId ||
      na[i].streamId !== nb[i].streamId ||
      na[i].streamUrl !== nb[i].streamUrl
    ) {
      return false;
    }
  }
  return true;
};

const ConfigLives = () => {
  const navigate = useNavigate();
  const [listContratos, setListContratos] = useState([]);
  const [filteredContratos, setFilteredContratos] = useState([]);
  const [activeStreams, setActiveStreams] = useState(readActiveStreamsFromSession());
  const [modalEnVivoOpen, setModalEnVivoOpen] = useState(false);
  const [contratoEnVivo, setContratoEnVivo] = useState(null);
  const [liveStreamUrl, setLiveStreamUrl] = useState("");
  const [liveStreamVersion, setLiveStreamVersion] = useState(0);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const ListarContratos = async () => {
    const routeSelected = "operaciones/getcontratostrafico";
    const res = await axios.get(`${process.env.REACT_APP_SERVER}/${routeSelected}`, {
      withCredentials: true,
    });
    if (res?.data?.success === false) return;
    const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
    setListContratos(rows);
    setFilteredContratos(rows);
    setActiveStreams((prev) => {
      if ((prev || []).length > 0) return prev;
      const seeded = buildActiveStreamsFromContratos(rows);
      if (seeded.length > 0) {
        syncActiveStreamsToStorage(seeded);
        return seeded;
      }
      return prev;
    });
  };

  const handleLiveNow = (msg) => {
    const deviceId = String(
      msg?.deviceId ||
      msg?.deviceID ||
      msg?.fkLokDeviceID ||
      msg?.FKLokDeviceID ||
      msg?.data?.deviceId ||
      msg?.data?.deviceID ||
      ""
    ).trim();
    const incomingStreamId = String(
      msg?.streamId ||
      msg?.streamID ||
      msg?.streamUrl ||
      msg?.streamURL ||
      msg?.url ||
      msg?.data?.streamId ||
      msg?.data?.streamUrl ||
      msg?.data?.url ||
      ""
    ).trim();
    if (!deviceId) return;

    const streamId = incomingStreamId || resolveLiveStreamUrl({ streamId: "", streamUrl: "", url: "" }, deviceId);

    setActiveStreams((prev) => {
      const exists = (prev || []).some(
        (stream) => String(stream?.deviceId || "") === deviceId && String(stream?.streamId || "") === streamId
      );
      if (exists) return prev;
      const next = [
        ...(prev || []),
        { deviceId, streamId, timestamp: msg?.timestamp || new Date().toISOString() },
      ];
      syncActiveStreamsToStorage(next);
      return next;
    });
  };

  const handleLiveEnded = (msg) => {
    const deviceId = String(
      msg?.deviceId ||
      msg?.deviceID ||
      msg?.fkLokDeviceID ||
      msg?.FKLokDeviceID ||
      msg?.data?.deviceId ||
      msg?.data?.deviceID ||
      ""
    ).trim();
    const streamId = String(
      msg?.streamId ||
      msg?.streamID ||
      msg?.streamUrl ||
      msg?.streamURL ||
      msg?.url ||
      msg?.data?.streamId ||
      msg?.data?.streamUrl ||
      msg?.data?.url ||
      ""
    ).trim();
    if (!deviceId && !streamId) return;

    setActiveStreams((prev) => {
      const next = (prev || []).filter((stream) => {
        const sameDevice = deviceId && String(stream?.deviceId || "") === deviceId;
        const sameStream = streamId && String(stream?.streamId || "") === streamId;
        if (streamId) return !sameStream;
        return !sameDevice;
      });
      syncActiveStreamsToStorage(next);
      return next;
    });
  };

  useEffect(() => {
    ListarContratos().catch(() => {});
    const syncActiveStreams = () => {
      const next = readActiveStreamsFromSession();
      setActiveStreams((prev) => (areStreamsEqual(prev, next) ? prev : next));
    };
    syncActiveStreams();
    const timer = setInterval(syncActiveStreams, 1500);
    const onStorage = (e) => {
      if (e.key === ACTIVE_STREAMS_STORAGE_KEY) {
        syncActiveStreams();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      clearInterval(timer);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const updateContratos = (next) => {
    if (Array.isArray(next)) {
      setFilteredContratos(next);
    }
  };

  const streamDevices = useMemo(
    () => new Set((activeStreams || []).map((s) => String(s?.deviceId || ""))),
    [activeStreams]
  );

  const liveContratos = useMemo(() => {
    if (!streamDevices.size) return filteredContratos;
    return (filteredContratos || []).filter((c) => streamDevices.has(String(c?.FKLokDeviceID || "")));
  }, [filteredContratos, streamDevices]);

  const abrirModalEnVivo = (contrato, streamUrl = "") => {
    const safeContrato = contrato?.FKLokDeviceID
      ? contrato
      : {
          FKLokDeviceID: String(contrato?.FKLokDeviceID || contrato?.deviceId || ""),
          NombreEmpresa: contrato?.NombreEmpresa || "N/D",
          DescripcionRuta: contrato?.DescripcionRuta || "N/D",
          TipoReporte: contrato?.TipoReporte || "En vivo",
          PlacaTruck: contrato?.PlacaTruck || "N/D",
        };
    setContratoEnVivo(safeContrato);
    setLiveStreamUrl(streamUrl || "");
    setLiveStreamVersion(Date.now());
    setModalEnVivoOpen(true);
  };

  const cerrarModalEnVivo = () => {
    setModalEnVivoOpen(false);
    setContratoEnVivo(null);
    setLiveStreamUrl("");
    setLiveStreamVersion(0);
  };

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <div className="layout-page">
          <BarraTrafico
            count={filteredContratos.length}
            filteredSolicitudes={filteredContratos}
            onUpdateSolicitudes={updateContratos}
            ListarContratos={ListarContratos}
            differTime={() => {}}
            onLiveNow={handleLiveNow}
            onLiveEnded={handleLiveEnded}
          />
          <Toaster position="bottom-right" />
          <div className="content-wrapper">
            <div className="container-fluid flex-grow-1 container-p-y">
              {isMobileView ? (
                <div className="card">
                  <div className="card-body text-center py-5">
                    <h5 className="mb-2">Vista solo para escritorio</h5>
                    <p className="text-muted mb-3">
                      La ruta <strong>/lives_config</strong> esta disponible para anchos de pantalla
                      de <strong> 993px o superiores</strong>.
                    </p>
                    <button className="btn btn-primary btn-sm" onClick={() => navigate("/trafico")}>
                      Ir a Trafico
                    </button>
                  </div>
                </div>
              ) : (
                <LivesGridManager
                  listContratos={liveContratos}
                  activeStreams={activeStreams}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <BaseModalXXL
        isOpen={modalEnVivoOpen}
        title="En vivo"
        onClose={cerrarModalEnVivo}
        fullscreen={isMobileView}
      >
        <EnVivoPanel
          contrato={contratoEnVivo}
          initialStreamUrl={liveStreamUrl}
          initialStreamVersion={liveStreamVersion}
          deviceActiveStreamUrls={(activeStreams || [])
            .filter(
              (stream) =>
                String(stream?.deviceId || "") ===
                String(contratoEnVivo?.FKLokDeviceID || "")
            )
            .map((stream) => String(stream?.streamId || "").trim())
            .filter(Boolean)}
          liveActive={(activeStreams || []).some(
            (stream) =>
              String(stream?.deviceId || "") ===
              String(contratoEnVivo?.FKLokDeviceID || "")
          )}
        />
      </BaseModalXXL>
    </div>
  );
};

export default ConfigLives;
